import { ReactNode, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Grid, Stack, Typography, useTheme } from '@mui/material'
import { CustomContainer } from '../../components/Wrapper/CustomContainer'
import { GridBreakpoint } from '../../interfaces/IGridSettings'
import { DashboardSnapshotDto, DrilldownKind } from '../../interfaces/IDashboard'
import { usePolling } from '../../hooks/usePolling'
import OpenByTypeCard from './components/OpenByTypeCard'
import OpenSingleCard from './components/OpenSingleCard'
import KpiBigNumber from './components/KpiBigNumber'
import PlannedDeviationsCard from './components/PlannedDeviationsCard'
import EntityListDialog from './components/EntityListDialog'
import MttrCard from './components/MttrCard'
import TimeToXCard from './components/TimeToXCard'
import QualityKpiCard from './components/QualityKpiCard'
import StatusFlowCard from './components/StatusFlowCard'
import OperatorActivityCard from './components/OperatorActivityCard'
import Delta from './components/Delta'
import { ALERT_WARN_COLOR, OVERDUE_WARN_COLOR } from './components/BucketColors'
import { INCIDENT_BUCKETS, INCIDENT_BUCKET_LABELS, PLANNED_BUCKETS } from './components/Buckets'

const DRILLDOWN_TITLES: Record<DrilldownKind, string> = {
  overdue: 'Hendelser med forfalt sluttid',
  'active-incident': 'Pågående fellesfeil',
  'incident-without-alert': 'Fellesfeil uten utsendt varsel',
  customers: 'Berørte kunder',
  servicepoints: 'Berørte leveransepunkter',
  'planned-deviations': 'Avvik på planlagt arbeid',
}

const INFO = {
  fellesfeil: 'Åpne fellesfeil fordelt på alvorlighet. A er høyest, C er lavest. Klikk et tall for å se hendelsene.',
  planlagt: 'Planlagte arbeider som ikke er ferdige, fordelt på alvorlighet. Klikk et tall for å se arbeidene.',
  generell: 'Åpne generelle hendelser. Klikk for å se hele lista.',
  totalt: 'Antall åpne hendelser totalt, uansett type. Pilen viser endring fra i går, og tallet ved siden av er gjennomsnittet de siste 7 dagene.',
  ffUtenVarsel: 'Fellesfeil som rammer kunder uten at det er sendt ut varsel. Krever oppfølging — klikk for å se hvilke.',
  forfalt: 'Hendelser der planlagt sluttid har passert. Bør enten lukkes eller forlenges. Klikk for å se lista. Tallet ved siden av er gjennomsnittet de siste 30 dagene som referanse — merk at det måles mot dagens sluttid, så en hendelse som er utsatt kan «forsvinne» fra snittet.',
  berørteKunder: 'Antall kunder som er rammet akkurat nå. Samme kunde teller én gang, selv om kunden er rammet av flere hendelser. Hendelser som er provisorisk løst, under evaluering eller oppfølging teller ikke med, og heller ikke planlagte arbeider som ennå ikke har startet. Klikk tallet for å se hvilke kunder som rammes — sortert med flest hendelser per kunde øverst.',
  berørteSP: 'Antall leveransepunkter som er rammet akkurat nå. Samme leveransepunkt teller én gang, selv om det er rammet av flere hendelser. Hendelser som er provisorisk løst, under evaluering eller oppfølging teller ikke med, og heller ikke planlagte arbeider som ennå ikke har startet.',
  pågåendeFF: 'Fellesfeil hvor kunder fremdeles er rammet. Hendelser som er provisorisk løst, under evaluering eller oppfølging vises ikke her — selv om de teknisk er åpne. Hendelser med starttid i fremtiden vises heller ikke.',
  kommendePA: 'Planlagte arbeider som starter framover i tid, nærmeste først. Klikk en rad for å åpne hendelsen.',
  pågåendeArbeid: 'Arbeid som montører jobber på akkurat nå. Klikkbar hvis arbeidet er koblet til en hendelse.',
  avvikPA: 'Avvik på lukkede planlagte arbeider som ikke er sjekket ut, nyeste først. Sjekk ut når behandlet.',
  statusFlyt: 'Median tid en fellesfeil tilbringer i hver status, basert på fellesfeil lukket de siste 30 dagene. Bredeste linje = der mest tid blir brukt. Brukes til å finne flaskehalser i arbeidsflyten. Lukket-fasen vises ikke fordi den bare ville fanget gjenåpninger; se "Gjenåpnet etter lukk" under kvalitetsmål.',
  kvalitet:
    'Fire kvalitetstall over de siste 30 dagene. Alvorlighetsgrad-stabilitet: andel fellesfeil som ble lukket uten at alvorlighetsgraden ble endret underveis — høyt tall betyr at førstevurderingen treffer. Lukket samme dag: andel hendelser som ble både opprettet og lukket innen samme kalenderdag — de raske vinnene. Varsler fullført: andel opprettede varsler som faktisk ble sendt; resten ble slettet før utsending. Gjenåpnet etter lukk: andel fellesfeil lukket i vinduet som har vært gjenåpnet minst én gang — signal på for tidlig lukking eller dobbeltregistrering.',
  operatørAktivitet:
    'De fem mest aktive operatørene siste 24 timer, rangert etter antall registrerte endringer på hendelser. Automatiske endringer fra systemet (for eksempel auto-utsendte varsler) regnes ikke med.',
  mttr: 'Median tid fra en hendelse ble opprettet til den ble lukket, for hendelser lukket de siste 30 dagene. Hendelser med åpenbart feil datofelt (lukke-tidspunkt før opprettelse) regnes ikke med.',
  timeToX:
    'Fire tidsmål for fellesfeil de siste 30 dagene. Tid til berørte: median tid fra hendelsen ble opprettet til operatør har lagt inn hvem som er rammet — selve triage-fasen. Tid til varsel: median tid fra berørte ble lagt inn til første varsel ble sendt. Fellesfeil uten kundepåvirkning teller ikke. Tid til første handling: median tid fra opprettelse til operatør gjør sin første endring på hendelsen, uansett hvilket felt. SLA: andel hendelser lukket innen planlagt sluttid — grønn ≥ 95 %, gul 80–95 %, rød < 80 %.',
} as const

function totalOpenSublabel(snapshot: DashboardSnapshotDto | null): ReactNode | undefined {
  if (!snapshot) return undefined
  return (
    <>
      i går: <Delta value={snapshot.totalOpen} reference={snapshot.totalOpen24hAgo} polarity="up-is-bad" /> · 7d snitt: {snapshot.totalOpen7dAverage.toFixed(1)}
    </>
  )
}

function overdueSublabel(snapshot: DashboardSnapshotDto | null): ReactNode | undefined {
  if (!snapshot) return undefined
  return (
    <>
      30d snitt: <Delta value={snapshot.overdueOpen} reference={snapshot.overdueOpen30dAverage} polarity="up-is-bad" />
    </>
  )
}

function DashboardOperator() {
  const navigate = useNavigate()
  const theme = useTheme()
  const [drilldownKind, setDrilldownKind] = useState<DrilldownKind | null>(null)

  const snapshot = usePolling<DashboardSnapshotDto>('/api/stats/dashboard/snapshot', 60_000)

  const goToFilteredEvents = (tab: 'incident' | 'planned', bucketKey: string, paramName: 'criticality' | 'category') => {
    navigate(`/?tab=${tab}&${paramName}=${encodeURIComponent(bucketKey)}`)
  }

  const goToGenericEvents = () => navigate('/?tab=generic')

  return (
    <CustomContainer gridWidth={GridBreakpoint.xxl}>
      <Stack spacing={3} sx={{ py: 2 }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4">Dashboard</Typography>
          {snapshot.lastUpdatedAt && (
            <Typography variant="caption" color="text.secondary">
              Sist oppdatert {snapshot.lastUpdatedAt.toLocaleTimeString('nb-NO')}
            </Typography>
          )}
        </Stack>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <OpenByTypeCard
              title="Fellesfeil"
              buckets={snapshot.data?.openIncidentByCriticality ?? null}
              bucketOrder={INCIDENT_BUCKETS}
              bucketLabels={INCIDENT_BUCKET_LABELS}
              typeAccentColor={theme.palette.eventType.fellesfeil}
              info={INFO.fellesfeil}
              onBucketClick={(bucket) => goToFilteredEvents('incident', bucket, 'criticality')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <OpenByTypeCard
              title="Planlagt arbeid"
              buckets={snapshot.data?.openPlannedByCriticality ?? null}
              bucketOrder={PLANNED_BUCKETS}
              typeAccentColor={theme.palette.eventType.planlagt}
              info={INFO.planlagt}
              onBucketClick={(bucket) => goToFilteredEvents('planned', bucket, 'criticality')}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <OpenSingleCard
              title="Generell hendelse"
              value={snapshot.data?.openGenericTotal ?? null}
              typeAccentColor={theme.palette.eventType.generell}
              info={INFO.generell}
              onClick={goToGenericEvents}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <KpiBigNumber
              label="Totalt åpne"
              value={snapshot.data?.totalOpen ?? null}
              sublabel={totalOpenSublabel(snapshot.data)}
              sparkline={snapshot.data?.totalOpenTrend24h}
              info={INFO.totalt}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <KpiBigNumber
              label="Fellesfeil uten varsel"
              value={snapshot.data?.openIncidentWithoutAlert ?? null}
              accentColor={(snapshot.data?.openIncidentWithoutAlert ?? 0) > 0 ? ALERT_WARN_COLOR : undefined}
              info={INFO.ffUtenVarsel}
              onClick={(snapshot.data?.openIncidentWithoutAlert ?? 0) > 0 ? () => setDrilldownKind('incident-without-alert') : undefined}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <KpiBigNumber
              label="Forfalt sluttid"
              value={snapshot.data?.overdueOpen ?? null}
              sublabel={overdueSublabel(snapshot.data)}
              sparkline={snapshot.data?.overdueOpenTrend24h}
              accentColor={(snapshot.data?.overdueOpen ?? 0) > 0 ? OVERDUE_WARN_COLOR : undefined}
              info={INFO.forfalt}
              onClick={(snapshot.data?.overdueOpen ?? 0) > 0 ? () => setDrilldownKind('overdue') : undefined}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <KpiBigNumber
              label="Berørte kunder"
              value={snapshot.data?.uniqueAffectedCustomers ?? null}
              info={INFO.berørteKunder}
              onClick={(snapshot.data?.uniqueAffectedCustomers ?? 0) > 0 ? () => setDrilldownKind('customers') : undefined}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <KpiBigNumber
              label="Berørte leveransepunkter"
              value={snapshot.data?.uniqueAffectedServicePoints ?? null}
              info={INFO.berørteSP}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <MttrCard info={INFO.mttr} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TimeToXCard info={INFO.timeToX} />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <StatusFlowCard info={INFO.statusFlyt} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <QualityKpiCard snapshot={snapshot.data} info={INFO.kvalitet} />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <OperatorActivityCard
              operators={snapshot.data?.topOperators24h ?? []}
              loaded={snapshot.data !== null}
              info={INFO.operatørAktivitet}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <PlannedDeviationsCard info={INFO.avvikPA} />
          </Grid>
        </Grid>

        <EntityListDialog
          open={drilldownKind !== null}
          onClose={() => setDrilldownKind(null)}
          title={drilldownKind ? DRILLDOWN_TITLES[drilldownKind] : ''}
          kind={drilldownKind}
        />
      </Stack>
    </CustomContainer>
  )
}

export default DashboardOperator
